import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CategoryIcon, pinStatus } from './MapPin';
import { tagText, deviceName } from '../lib/assets';

/**
 * DESKS ON THE MAP (108): ONE PIN PER DESK, replacing the pins of its kit.
 *
 * Gordon, 28 October, from the proposal: a desk's monitors, dock, keyboard
 * and phone are one place on the plan, not five pins on top of each other.
 * The server leaves a desk's kit out of the map's device list and sends
 * each desk with its kit (routes/asset-discovery.js); this draws them:
 *
 *   DeskPin    the desk's pin: a desk icon in the worst ring of its kit
 *              (grey when it has none), its kit count, and on hover or
 *              focus a card saying who it is for - the fixed person, "hot
 *              desk", or (to an IT administrator) who is at it today - with
 *              the kit listed; a piece of kit chooses it on the map (the
 *              desk's kit opens on the right, the piece lit), and chosen
 *              already, opens its page (Gordon, 4 November)
 *   DeskList   the desks beneath this place, each with its kit beneath it,
 *              Place / Move / Unpin for a writer, the way the device list
 *              does it for a device. The device list itself is untouched.
 *
 * A person's own things at a desk keep their own pins and their own lines.
 */
const RANK = { critical: 3, warning: 2, idle: 1, success: 0 };

/** The worst ring of the desk's kit; grey for an empty desk. */
export const deskTone = (desk) => (desk.kit || []).map((d) => pinStatus(d).tone)
  .sort((a, b) => RANK[b] - RANK[a])[0] || 'idle';

/** Who the desk is for, in a few words: "Rita Gatt", "Hot desk · today Omar Vella", "Free". */
export function deskWho(desk) {
  if (desk.desk_mode === 'hot') return desk.today ? `Hot desk · today ${desk.today}` : 'Hot desk · free';
  if (desk.desk_user_name) return `${desk.desk_user_name}${desk.desk_user_left ? ' (has left)' : ''}`;
  return desk.today ? `Unassigned · today ${desk.today}` : 'Unassigned · free';
}

const DeskIcon = () => (
  <svg className="as-pin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10h18 M5 10v9 M19 10v9 M8 6h8v4H8z" />
  </svg>
);

/** As the map's groups (pages/AssetMap.js): the card stays this long after the pointer leaves, for the way across. */
const CLOSE_MS = 200;
const EDGE_PX = 170;

/**
 * `width`: the plan's drawn width in pixels, so a desk near a side opens
 * its card away from it. `selected`: the device chosen on the map; `onKit(d)`
 * chooses a piece of kit (or, chosen already, opens it).
 */
export function DeskPin({ desk, at, hover, onHover, onOpen, width = 0, selected = null, onKit }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const hold = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), CLOSE_MS); };
  const tone = deskTone(desk);
  const pct = (v) => `${v * 100}%`;
  const kit = desk.kit || [];
  const px = at.x * width;
  const side = !width ? 'middle' : px < EDGE_PX ? 'start' : width - px < EDGE_PX ? 'end' : 'middle';
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span className="as-pin-group as-desk-pin" data-testid="desk-pin" data-desk={desk.id} data-open={String(open)}
      data-flip={String(at.y > 0.55)} data-side={side} style={{ left: pct(at.x), top: pct(at.y) }}
      onMouseDown={(ev) => ev.stopPropagation()}
      onClick={(ev) => ev.stopPropagation()}
      onPointerEnter={(ev) => { if (ev.pointerType === 'mouse') hold(); onHover?.(desk.id); }}
      onPointerLeave={(ev) => { if (ev.pointerType === 'mouse') leave(); onHover?.(null); }}
      onFocus={hold}
      onBlur={(ev) => { if (!ev.currentTarget.contains(ev.relatedTarget)) leave(); }}
      onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); setOpen(false); } }}>
      <button type="button" className="as-pin as-pin-groupbtn" data-tone={tone} data-fixed="true" data-hover={String(hover === desk.id)}
        aria-label={`${desk.name}: ${deskWho(desk)}, ${kit.length} piece${kit.length === 1 ? '' : 's'} of kit`}
        onClick={() => onOpen(desk)}>
        <DeskIcon />
        <span className="as-pin-count">{kit.length}</span>
      </button>
      {open && (
        <div className="as-pin-pop" role="group" aria-label={`${desk.name} and its kit`} data-testid="desk-pop">
          <p className="as-pin-pop-sum"><strong>{desk.name}</strong>{` · ${deskWho(desk)}`}</p>
          {kit.length ? (
            <ul>
              {kit.map((d) => {
                const st = pinStatus(d);
                return (
                  <li key={d.id}>
                    <button type="button" data-testid="desk-kit-item" data-device={d.id} data-selected={String(selected === d.id)}
                      onClick={() => {
                        const already = selected === d.id;
                        onKit?.(d);
                        if (!already) { clearTimeout(timer.current); setOpen(false); }
                      }}>
                      <span className="as-pin-badge" data-tone={st.tone} data-fixed="true" title={`${st.label} · fixed`}><CategoryIcon category={d.category_name} /></span>
                      <strong>{d.category_name}</strong>
                      {deviceName(d) !== d.category_name ? ` · ${deviceName(d)}` : ''}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : <p className="as-quiet">No kit at this desk.</p>}
        </div>
      )}
    </span>
  );
}

/**
 * `sizing` (111) is the desk being sized, or null; undefined when sizes are
 * not here yet (before migration 111), and then "Size" is not offered.
 *
 * `open` / `onToggle`: whether the list is unfolded is the map's, so a piece
 * of kit chosen on the plan can open it (4 November); `selected` is that
 * piece, lit in its desk's kit. `onOpenDevice(id)` is the click handler for
 * a piece's link (the map's, so "← Map" comes back to it chosen).
 */
export function DeskList({
  desks, placeId, onThisPlan, write, shown, placing, hover, onHover, onPlace, onUnpin, sizing, onSize, onUnsize,
  open: openGiven, onToggle, selected = null, onOpenDevice
}) {
  // Folded until opened, every visit: not remembered, so it is closed again next time.
  const [openOwn, setOpenOwn] = useState(false);
  const open = openGiven === undefined ? openOwn : openGiven;
  const toggle = onToggle || (() => setOpenOwn((v) => !v));
  if (!desks.length) return null;
  return (
    <section className="as-map-half" aria-label="Desks" data-testid="desk-list">
      <h3 className="as-map-halfhead">
        <button type="button" className="as-map-halfbtn" aria-expanded={open} onClick={toggle} data-testid="desks-head">
          <span aria-hidden="true">{open ? '▾ ' : '▸ '}</span>{`Desks (${desks.length})`}
        </button>
      </h3>
      {open && (
      <ul className="as-tree">
        {desks.map((k) => {
          const here = onThisPlan(k);
          const placed = here && k.map_x !== null && k.map_x !== undefined;
          return (
            <li key={k.id} className="as-desk-line" data-hover={String(hover === k.id)}
              onMouseEnter={() => onHover(k.id)} onMouseLeave={() => onHover(null)}>
              <div className="as-desk-head">
                <span className="as-pin-badge" data-tone={deskTone(k)} data-fixed="true"><DeskIcon /></span>
                {here || !k.plan_location_id
                  ? <span className="as-chip" data-tone={placed ? 'success' : 'idle'}>{placed ? 'Placed' : 'Not placed'}</span>
                  : <Link className="as-chip" data-tone="info" to={`/assets/map/${k.plan_location_id}`}>{`On ${k.plan_location_name}’s plan`}</Link>}
                <Link to={`/assets/desks/${k.id}`}><strong>{k.name}</strong></Link>
                <span className="as-quiet">
                  {` ${deskWho(k)} · ${k.kit_count} piece${k.kit_count === 1 ? '' : 's'} of kit`}
                  {k.parent_id !== placeId && k.room_name ? ` · ${k.room_name}` : ''}
                </span>
                <span className="as-spacer" />
                {write && shown && here && (
                  <>
                    <button type="button" className="btn btn-plain btn-small" disabled={placing === k.id}
                      aria-label={`${placed ? 'Move' : 'Place'} ${k.name}`} onClick={() => onPlace(k.id)}>{placed ? 'Move' : 'Place'}</button>
                    {placed && <button type="button" className="btn btn-plain btn-small" onClick={() => onUnpin(k.id)}>Unpin</button>}
                    {sizing !== undefined && (
                      <button type="button" className="btn btn-plain btn-small" disabled={sizing === k.id}
                        aria-label={`${k.map_w ? 'Resize' : 'Size'} ${k.name}`} title="Drag a rectangle on the plan for the desk’s size"
                        onClick={() => onSize(k.id)}>{k.map_w ? 'Resize' : 'Size'}</button>
                    )}
                    {sizing !== undefined && k.map_w && (
                      <button type="button" className="btn btn-plain btn-small" aria-label={`Remove the size of ${k.name}`} onClick={() => onUnsize(k.id)}>Pin only</button>
                    )}
                  </>
                )}
              </div>
              {(k.kit || []).length > 0 && (
                <ul className="as-tree as-desk-kit">
                  {k.kit.map((d) => (
                    <li key={d.id} data-device={d.id} data-selected={String(selected === d.id)}>
                      <span className="as-pin-badge" data-tone={pinStatus(d).tone} data-fixed="true" title={`${pinStatus(d).label} · fixed`}><CategoryIcon category={d.category_name} /></span>
                      <Link to={`/assets/${d.id}`} onClick={onOpenDevice ? onOpenDevice(d.id) : undefined}><strong>{tagText(d)}</strong></Link>
                      <span className="as-quiet">{` ${[d.category_name, d.manufacturer, d.model].filter(Boolean).join(' ')}`}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      )}
    </section>
  );
}
